import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { canSeeArchivedBills } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  TooltipProvider, Tooltip, TooltipTrigger, TooltipContent,
} from '../../components/ui/tooltip';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { MissingBillsHelpDrawer } from '../../components/intake/MissingBillsHelpDrawer';
import {
  MailWarning, RefreshCw, Download, Loader2, Info, MessageSquare,
  CheckCircle2, Clock3, Send, FileText, Bot, DownloadCloud, FileDown, Zap, Upload,
  HelpCircle, Search, X,
} from 'lucide-react';

const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const CADENCE_LABEL = {
  monthly: 'Monthly', quarterly: 'Quarterly', need_based: 'Need-based', unknown: 'Unknown',
};

const cadenceReason = (r) => {
  const label = CADENCE_LABEL[r.inferred_cadence] || r.inferred_cadence;
  const pd = r.period_days;
  if (r.inferred_cadence === 'need_based') {
    return 'Need-based — no billing period on bills and no regular arrival pattern, so it is never auto-flagged as Missing.';
  }
  switch (r.cadence_source) {
    case 'frequency':
      return `${label} — from the account's declared billing frequency.`;
    case 'period':
      return `${label} — derived from the bill's billing period (~${pd}-day cycle).`;
    case 'inferred':
      return `${label} — inferred from the pattern of past bill arrival dates (~${pd}-day cycle).`;
    default:
      return `${label} cadence (${pd}-day cycle).`;
  }
};

function DaysBadge({ days, severity }) {
  if (!days && days !== 0) return <span className="text-muted-foreground">0</span>;
  const cls = severity === 'red'
    ? 'bg-red-100 text-red-700 border-red-200'
    : severity === 'amber'
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : severity === 'green'
        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
        : 'bg-muted text-muted-foreground';
  return <Badge variant="outline" className={cls}>{days}d</Badge>;
}

function BillSourceBadge({ source }) {
  if (!source) return <span className="text-muted-foreground text-sm">{'\u2014'}</span>;
  const s = String(source).trim();
  const key = s.toLowerCase();
  const cls = key === 'portal'
    ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
    : key === 'email'
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : key === 'ebill' || key === 'e-bill'
        ? 'bg-sky-100 text-sky-700 border-sky-200'
        : 'bg-muted text-muted-foreground';
  return <Badge variant="outline" className={cls}>{s}</Badge>;
}

export default function MissingBills() {
  const [currentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  const accountId = currentAccount?.id;

  const [tab, setTab] = useState('late_arriving');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [users, setUsers] = useState([]);

  // global filters
  const [opts, setOpts] = useState({ vendors: [], account_numbers: [], properties: [], in_validation_count: 0, late_arriving_count: 0, missing_count: 0 });
  const [fVendor, setFVendor] = useState('all');
  const [fAccount, setFAccount] = useState('all');
  const [fProperty, setFProperty] = useState('all');
  const [fSeverity, setFSeverity] = useState('all');
  const [fAssigned, setFAssigned] = useState('all');
  const [fInVal, setFInVal] = useState('all');
  const [fAutomated, setFAutomated] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const [settings, setSettings] = useState(null);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const canSeeArchived = canSeeArchivedBills();

  // help drawer
  const [helpOpen, setHelpOpen] = useState(false);

  // notes drawer
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesRow, setNotesRow] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // bill PDF drawer (slides from left)
  const [billOpen, setBillOpen] = useState(false);
  const [billRow, setBillRow] = useState(null);
  const [billPdf, setBillPdf] = useState(null);
  const [billUrl, setBillUrl] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [collectorIndex, setCollectorIndex] = useState({ by_account_number: {} });
  const [collectingRow, setCollectingRow] = useState(null);
  const [downloadingRow, setDownloadingRow] = useState(null);

  const PAGE_SIZE = 50;

  const listParams = useCallback((offset) => ({
    account_id: accountId, flag: tab,
    vendor_name: fVendor !== 'all' ? fVendor : undefined,
    account_number: fAccount !== 'all' ? fAccount : undefined,
    property_id: fProperty !== 'all' ? fProperty : undefined,
    severity: fSeverity !== 'all' ? fSeverity : undefined,
    assigned_to: fAssigned !== 'all' ? fAssigned : undefined,
    in_validation: fInVal !== 'all' ? fInVal : undefined,
    automated: fAutomated !== 'all' ? fAutomated : undefined,
    search: fSearch.trim() ? fSearch.trim() : undefined,
    include_historical: showHistorical || undefined,
    show_snoozed: showSnoozed, limit: PAGE_SIZE, offset,
  }), [accountId, tab, fVendor, fAccount, fProperty, fSeverity, fAssigned, fInVal, fAutomated, fSearch, showSnoozed, showHistorical]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/intake/missing-bills/list', { params: listParams(0) });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load missing bills');
    } finally {
      setLoading(false);
    }
  }, [accountId, listParams]);

  useEffect(() => { load(); }, [load]);

  // Debounce the free-text search box (account # / vendor) before querying.
  useEffect(() => {
    const t = setTimeout(() => setFSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadStats = useCallback(() => {
    if (!accountId) return;
    api.get('/intake/missing-bills/stats', { params: { account_id: accountId } })
      .then(({ data }) => setStats(data)).catch(() => {});
  }, [accountId]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (!accountId) return;
    api.get('/intake/missing-bills/assignable-users', { params: { account_id: accountId } })
      .then(({ data }) => setUsers(data.users || [])).catch(() => {});
    api.get('/intake/missing-bills/filter-options', { params: { account_id: accountId, include_historical: showHistorical || undefined } })
      .then(({ data }) => setOpts(data || { vendors: [], account_numbers: [], properties: [] }))
      .catch(() => {});
    api.get('/intake/missing-bills/settings', { params: { account_id: accountId } }).then(({ data }) => { setSettings(data); }).catch(() => {});
    api.get('/portal-collectors/credential-index', { params: { account_id: accountId } })
      .then(({ data }) => setCollectorIndex(data || { by_account_number: {} })).catch(() => {});
  }, [accountId, showHistorical]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get('/intake/missing-bills/list', { params: listParams(rows.length) });
      setRows((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const refreshOptions = () =>
    api.get('/intake/missing-bills/filter-options', { params: { account_id: accountId, include_historical: showHistorical || undefined } })
      .then(({ data }) => setOpts(data)).catch(() => {});

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post('/intake/missing-bills/recompute', {}, { params: { account_id: accountId, include_historical: showHistorical || undefined } });
      toast.success(`Recomputed · ${data.late_arriving} late · ${data.missing} missing · ${data.in_validation ?? 0} in-validation · ${data.units_marked_occupied} units→occupied · ${data.accounts_ignored} ignored${data.purged_inactive ? ` · ${data.purged_inactive} inactive cleared` : ''}`);
      await load(); await refreshOptions(); loadStats();
    } catch {
      toast.error('Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  // Toggling historical ON runs a one-time backward recompute (to the org's
  // Missing Bill Start Date) so periods before it are generated, then reveals
  // them. The main counts always stay anchored to the start date.
  const toggleHistorical = async (next) => {
    if (next) {
      setHistoricalLoading(true);
      try {
        await api.post('/intake/missing-bills/recompute', {}, { params: { account_id: accountId, include_historical: true } });
      } catch {
        toast.error('Historical recompute failed');
        setHistoricalLoading(false);
        return;
      }
      setHistoricalLoading(false);
    }
    setShowHistorical(next);
    loadStats();
  };

  const assign = async (row, userId) => {
    try {
      const { data } = await api.patch(`/intake/missing-bills/${row.id}/assign`,
        { assigned_to: userId === 'unassigned' ? null : userId },
        { params: { account_id: accountId } });
      setRows((prev) => prev.map((r) => (r.id === row.id ? data : r)));
      loadStats();
    } catch {
      toast.error('Assignment failed');
    }
  };

  const snooze = async (row) => {
    try {
      await api.post(`/intake/missing-bills/${row.id}/snooze`, {}, { params: { account_id: accountId } });
      toast.success('Snoozed 7 days');
      await load();
    } catch { toast.error('Snooze failed'); }
  };

  const markReceived = async (row) => {
    try {
      await api.post(`/intake/missing-bills/${row.id}/mark-received`, {}, { params: { account_id: accountId } });
      toast.success('Marked received & archived');
      await load();
    } catch { toast.error('Mark received failed'); }
  };

  const runCollect = async (row) => {
    const cred = collectorIndex.by_account_number?.[String(row.vendor_account_number)];
    if (!cred) return;
    setCollectingRow(row.id);
    try {
      const { data: run } = await api.post(
        `/portal-collectors/credentials/${cred.credential_id}/run`, {},
        { params: { account_id: accountId } },
      );
      toast.info(run.message || 'Bill collection started…');
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data: r } = await api.get(`/portal-collectors/runs/${run.id}`, { params: { account_id: accountId } });
          if (r.status === 'in_progress' && attempts < 15) { setTimeout(poll, 8000); return; }
          if (r.status === 'completed') { toast.success(`Collected ${r.docs_ingested || 0} bill(s) → Intake Hub`); await load(); }
          else if (r.status === 'failed') toast.error(`Collection failed: ${r.error || 'error'}`);
          else toast.info('Collection running in the background');
          setCollectingRow(null);
        } catch { setCollectingRow(null); }
      };
      setTimeout(poll, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start collection');
      setCollectingRow(null);
    }
  };

  // Fire-and-forget: ask the collector to fetch this specific missing bill by
  // account number. The user is notified in-app when the bill later arrives.
  const triggerDownload = async (row) => {
    setDownloadingRow(row.id);
    try {
      const { data } = await api.post(`/intake/missing-bills/${row.id}/trigger-download`, {},
        { params: { account_id: accountId } });
      if (data?.actionable === false) {
        toast.warning(data.message || "The provider couldn't fetch this bill automatically.",
          { duration: 8000 });
      } else {
        toast.success(data?.message || "Download requested — we'll notify you when the bill arrives.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to trigger download');
    } finally {
      setDownloadingRow(null);
    }
  };

  // ── Upload a missing bill PDF → forwarded to the BillPortal processing engine ──
  const uploadInputRef = useRef(null);
  const uploadRowRef = useRef(null);
  const [uploadingRow, setUploadingRow] = useState(null);

  const openUpload = (row) => {
    uploadRowRef.current = row;
    uploadInputRef.current?.click();
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const row = uploadRowRef.current;
    if (!file || !row) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    setUploadingRow(row.id);
    try {
      const form = new FormData();
      form.append('files', file);
      if (row.vendor_name) form.append('supplier_name', row.vendor_name);
      const { data } = await api.post('/payments/v2/emergency-bill-upload', form, {
        params: { account_id: accountId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const res = (data?.uploaded || [])[0];
      if (res && (res.status === 'accepted_by_engine' || res.status === 'uploaded_pending_lookup')) {
        toast.success(`Bill uploaded for ${row.vendor_name || 'this account'} — sent to the processing engine.`);
      } else {
        toast.error(res?.error || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload bill');
    } finally {
      setUploadingRow(null);
    }
  };

  const openNotes = async (row) => {
    setNotesRow(row); setNotesOpen(true); setNotes([]); setNoteBody('');
    try {
      const { data } = await api.get(`/intake/missing-bills/${row.id}/notes`, { params: { account_id: accountId } });
      setNotes(data.notes || []);
    } catch { toast.error('Failed to load notes'); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/intake/missing-bills/${notesRow.id}/notes`,
        { body: noteBody.trim() }, { params: { account_id: accountId } });
      setNotes((prev) => [data, ...prev]);
      setNoteBody('');
      setRows((prev) => prev.map((r) => (r.id === notesRow.id
        ? { ...r, notes_count: (r.notes_count || 0) + 1 } : r)));
    } catch { toast.error('Failed to add note'); }
    finally { setSavingNote(false); }
  };

  const openBill = async (row) => {
    if (!row.latest_bill_id) { toast.info('No bill linked'); return; }
    setBillRow(row); setBillOpen(true); setBillPdf(null); setBillUrl(null); setBillLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/bill-pdf/${row.latest_bill_id}`,
        { params: { account_id: accountId } });
      if (data?.base64_data) setBillPdf(data.base64_data);
      else if (data?.pdf_url) setBillUrl(data.pdf_url);
    } catch {
      setBillPdf(null); setBillUrl(null);
    } finally { setBillLoading(false); }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await api.get('/intake/missing-bills/export.csv', {
        params: { ...listParams(0), limit: undefined, offset: undefined }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.download = `missing_bills_${tab}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const billPdfUrl = billPdf ? `data:application/pdf;base64,${billPdf}` : (billUrl || null);

  return (
    <MainLayout>
      <TooltipProvider>
        <div className="space-y-5" data-testid="missing-bills-page">
          <MissingBillsHelpDrawer open={helpOpen} onOpenChange={setHelpOpen} />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <MailWarning className="h-6 w-6 text-primary" /> Missing Bills
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="mb-info-tooltip">
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm text-xs leading-relaxed space-y-2">
                    <p>
                      Each missing billing period is tracked as its own row.{' '}
                      {settings && (settings.compute_unit_bills === false
                        ? 'Tracking Common Area bills only — unit-level tracking is off (enable it in Settings → Missing Bills).'
                        : 'Tracking Common Area and unit bills.')}{' '}
                      Units with no bill for 45+ days auto-flip to occupied; occupied units stop being flagged 30 days after occupancy.
                    </p>
                    {settings?.effective_start_date && (
                      <p data-testid="mb-start-date-note">
                        Counting missing bills from your Missing Bill Start Date:{' '}
                        <span className="font-semibold">{fmtDate(settings.effective_start_date)}</span>
                        {settings.missing_bill_start_date ? '' : ' (org creation date — set a custom date in Settings → Missing Bills)'}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </h1>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setHelpOpen(true)}
                data-testid="mb-help-btn">
                <HelpCircle className="h-4 w-4 mr-1" /> How it works
              </Button>
              <Button variant="outline" size="sm" onClick={downloadCsv}
                data-testid={`mb-csv-download-${tab === 'late_arriving' ? 'late' : 'missing'}`}>
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
              <Button size="sm" onClick={recompute} disabled={recomputing} data-testid="mb-recompute-btn">
                {recomputing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Recompute
              </Button>
            </div>
          </div>

          {/* Top-level stats cards (clickable Late/Missing switch tabs) */}
          <div className={`grid grid-cols-2 md:grid-cols-3 ${canSeeArchived ? 'lg:grid-cols-7' : 'lg:grid-cols-6'} gap-3`} data-testid="mb-stats">
            <Card
              className={`p-4 cursor-pointer transition-colors ${tab === 'late_arriving' ? 'border-primary ring-1 ring-primary/30' : 'hover:border-primary/40'}`}
              onClick={() => setTab('late_arriving')}
              data-testid="mb-stat-late-arriving"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5 text-amber-500" /> Late Arriving
              </div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-late-arriving-count">
                {stats ? stats.late_arriving.total : '\u2014'}
              </div>
              {stats && (
                <div className="mt-1 text-[11px] text-muted-foreground" data-testid="mb-stat-late-arriving-breakdown">
                  Common Area {stats.late_arriving.common_area ?? 0} · Units {stats.late_arriving.unit ?? 0}
                </div>
              )}
              {stats && ((stats.late_arriving.green ?? 0) > 0 || stats.late_arriving.amber > 0) && (
                <div className="mt-1.5 flex gap-1.5" data-testid="mb-stat-late-arriving-severity">
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]">{stats.late_arriving.green ?? 0} green</Badge>
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-[10px]">{stats.late_arriving.amber} amber</Badge>
                </div>
              )}
            </Card>

            <Card
              className={`p-4 cursor-pointer transition-colors ${tab === 'missing' ? 'border-primary ring-1 ring-primary/30' : 'hover:border-primary/40'}`}
              onClick={() => setTab('missing')}
              data-testid="mb-stat-missing"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <MailWarning className="h-3.5 w-3.5 text-rose-500" /> Missing
              </div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-missing-count">
                {stats ? stats.missing.total : '\u2014'}
              </div>
              {stats && (
                <div className="mt-1 text-[11px] text-muted-foreground" data-testid="mb-stat-missing-breakdown">
                  Common Area {stats.missing.common_area ?? 0} · Units {stats.missing.unit ?? 0}
                </div>
              )}
              {stats && stats.missing.red > 0 && (
                <div className="mt-1.5 flex gap-1.5" data-testid="mb-stat-missing-severity">
                  <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700 text-[10px]">{stats.missing.red} overdue</Badge>
                </div>
              )}
            </Card>

            <Card className="p-4" data-testid="mb-stat-total">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total Open</div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-total-count">{stats ? stats.total : '\u2014'}</div>
            </Card>

            <Card className="p-4" data-testid="mb-stat-assigned">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Assigned
              </div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-assigned-count">{stats ? stats.assigned : '\u2014'}</div>
            </Card>

            <Card className="p-4" data-testid="mb-stat-unassigned">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Unassigned</div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-unassigned-count">{stats ? stats.unassigned : '\u2014'}</div>
            </Card>

            {canSeeArchived && (
              <Card className="p-4" data-testid="mb-stat-archived">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Archived</div>
                <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-archived-count">{stats ? stats.archived : '\u2014'}</div>
              </Card>
            )}

            <Card
              className={`p-4 cursor-pointer transition-colors ${showHistorical ? 'border-slate-400 ring-1 ring-slate-300' : 'hover:border-slate-300'}`}
              onClick={() => toggleHistorical(!showHistorical)}
              data-testid="mb-stat-historical"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5 text-slate-400" /> Historical
              </div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-historical-count">
                {stats ? (stats.historical ?? 0) : '\u2014'}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                {showHistorical ? 'shown' : 'pre-start · hidden'}
              </div>
            </Card>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="late_arriving" data-testid="missing-bills-tab-late">Late Arriving Bills</TabsTrigger>
              <TabsTrigger value="missing" data-testid="missing-bills-tab-missing">Missing Bills</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Global filters (server-side, apply across all pages) */}
          <div className="flex flex-wrap items-center gap-3" data-testid="mb-filters">
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search account # or vendor…"
                className="pl-9 pr-8 h-10"
                data-testid="mb-search-input"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="mb-search-clear"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Select value={fProperty} onValueChange={setFProperty}>
              <SelectTrigger className="w-48" data-testid="mb-filter-property"><SelectValue placeholder="Property" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {opts.properties.map((p) => <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fVendor} onValueChange={setFVendor}>
              <SelectTrigger className="w-44" data-testid="mb-filter-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {opts.vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fAccount} onValueChange={setFAccount}>
              <SelectTrigger className="w-44" data-testid="mb-filter-account"><SelectValue placeholder="Account #" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All accounts</SelectItem>
                {opts.account_numbers.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fSeverity} onValueChange={setFSeverity}>
              <SelectTrigger className="w-36" data-testid="mb-filter-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any severity</SelectItem>
                <SelectItem value="red">Red (overdue / missing)</SelectItem>
                <SelectItem value="amber">Amber (about to flip)</SelectItem>
                <SelectItem value="green">Green (recently late)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fAssigned} onValueChange={setFAssigned}>
              <SelectTrigger className="w-44" data-testid="mb-filter-assigned"><SelectValue placeholder="Assignee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All assignees</SelectItem>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fInVal} onValueChange={setFInVal}>
              <SelectTrigger className="w-52" data-testid="mb-filter-in-validation">
                <SelectValue placeholder="Validation status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="only">In validation only</SelectItem>
                <SelectItem value="exclude">Exclude in-validation</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fAutomated} onValueChange={setFAutomated}>
              <SelectTrigger className="w-48" data-testid="mb-filter-automated">
                <SelectValue placeholder="Automation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                <SelectItem value="yes">Automated only</SelectItem>
                <SelectItem value="no">Not automated only</SelectItem>
              </SelectContent>
            </Select>
            {opts.in_validation_count > 0 && (
              <button
                type="button"
                onClick={() => setFInVal(fInVal === 'only' ? 'all' : 'only')}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                  fInVal === 'only'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                }`}
                data-testid="mb-in-validation-chip"
              >
                <Loader2 className={`h-3.5 w-3.5 ${fInVal === 'only' ? 'animate-spin' : ''}`} />
                In Validation · {opts.in_validation_count}
              </button>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="mb-show-snoozed">
              <Checkbox checked={showSnoozed} onCheckedChange={(v) => setShowSnoozed(!!v)} /> Show snoozed
            </label>
            <Tooltip>
              <TooltipTrigger asChild>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="mb-show-historical">
                  <Checkbox
                    checked={showHistorical}
                    disabled={historicalLoading}
                    onCheckedChange={(v) => toggleHistorical(!!v)}
                  />
                  {historicalLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Show historical
                  {opts.historical_count > 0 && (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]" data-testid="mb-historical-count">
                      {opts.historical_count}
                    </Badge>
                  )}
                </label>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Goes backwards before your Missing Bill Start Date
                {settings?.effective_start_date ? ` (${fmtDate(settings.effective_start_date)})` : ''} and
                recomputes the historical missing periods. These are excluded from your
                default client-facing counts so older gaps don't alarm the client.
              </TooltipContent>
            </Tooltip>
          </div>

          <input
            ref={uploadInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={onUploadFile}
            data-testid="mb-upload-input"
          />
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Unit / Address</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Account #</TableHead>
                  <TableHead>Bill Source</TableHead>
                  <TableHead>
                    <span className="inline-flex items-center gap-1">
                      Latest Bill
                      <Tooltip>
                        <TooltipTrigger asChild><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger>
                        <TooltipContent>Latest received bill issue date for the account &amp; vendor. Click the icon to view the PDF.</TooltipContent>
                      </Tooltip>
                    </span>
                  </TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Cadence</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [1, 2, 3].map((i) => (
                    <TableRow key={i}><TableCell colSpan={12}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-10">
                    <MailWarning className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <div className="font-medium text-foreground">No {tab === 'late_arriving' ? 'late-arriving' : 'missing'} bills</div>
                    <div className="mt-1">
                      We forecast expected bills from each account's billing cadence.
                      Click <strong>Recompute</strong> to build the latest forecast for this org.
                    </div>
                  </TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} data-testid={`mb-row-${r.id}`}>
                    <TableCell className="font-medium">{r.property_name || '\u2014'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.unit_number ? <span className="font-medium text-foreground">Unit {r.unit_number}</span> : null}
                      {r.unit_number && r.service_address ? ' · ' : ''}
                      {r.service_address || (!r.unit_number ? '\u2014' : '')}
                    </TableCell>
                    <TableCell>{r.vendor_name}</TableCell>
                    <TableCell>{r.vendor_account_number}</TableCell>
                    <TableCell data-testid={`mb-bill-source-${r.id}`}>
                      <BillSourceBadge source={r.bill_source} />
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5">
                        {fmtDate(r.latest_bill_issue_date)}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => openBill(r)} className="text-primary hover:text-primary/70 transition-colors"
                              data-testid={`mb-bill-icon-${r.id}`}>
                              <FileText className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>View latest bill PDF</TooltipContent>
                        </Tooltip>
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{fmtDate(r.expected_issue_date)}</TableCell>
                    <TableCell>
                      {r.in_validation ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline"
                              className="bg-blue-100 text-blue-700 border-blue-200 cursor-help"
                              data-testid={`mb-inval-${r.id}`}>
                              In Validation
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            A bill arrived for this account and is awaiting validation
                            ({r.in_validation_status || 'in review'}) — it is in the pipeline,
                            not truly missing. {r.days_past_expected}d past expected.
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <DaysBadge days={r.days_past_expected} severity={r.severity} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="secondary" className="cursor-help" data-testid={`mb-cadence-${r.id}`}>
                            {CADENCE_LABEL[r.inferred_cadence] || r.inferred_cadence}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{cadenceReason(r)}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Select value={r.assigned_to || 'unassigned'} onValueChange={(v) => assign(r, v)}>
                        <SelectTrigger className="w-40 h-8" data-testid={`mb-assign-${r.id}`}>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openNotes(r)} data-testid={`mb-notes-${r.id}`}>
                        <MessageSquare className="h-4 w-4 mr-1" /> {r.notes_count || 0}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {collectorIndex.by_account_number?.[String(r.vendor_account_number)] && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => runCollect(r)} disabled={collectingRow === r.id} data-testid={`mb-collect-${r.id}`}>
                              {collectingRow === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4 text-blue-600" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Collect latest bill from vendor portal</TooltipContent>
                        </Tooltip>
                      )}
                      {r.automated === true && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2.5 text-xs gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                              onClick={() => triggerDownload(r)}
                              disabled={downloadingRow === r.id}
                              data-testid={`mb-trigger-download-${r.id}`}
                            >
                              {downloadingRow === r.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <FileDown className="h-3.5 w-3.5" />}
                              Fetch bill
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Ask the vendor portal to fetch this missing bill</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openUpload(r)}
                            disabled={uploadingRow === r.id}
                            data-testid={`mb-upload-bill-${r.id}`}
                          >
                            {uploadingRow === r.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Upload className="h-4 w-4 text-slate-600" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Upload this bill (PDF) to the processing engine</TooltipContent>
                      </Tooltip>
                      {/* Snooze action hidden per product request
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => snooze(r)} data-testid={`mb-snooze-${r.id}`}>
                            <Clock3 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Snooze 7 days</TooltipContent>
                      </Tooltip>
                      */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => markReceived(r)} data-testid={`mb-mark-received-${r.id}`}>
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Mark received (archive)</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {!loading && rows.length > 0 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="mb-pagination">
              <span>Showing {rows.length} of {total}</span>
              {rows.length < total && (
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="mb-load-more">
                  {loadingMore ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Load more ({total - rows.length} left)
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Notes drawer (right) */}
        <Sheet open={notesOpen} onOpenChange={setNotesOpen}>
          <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="mb-notes-drawer">
            <SheetHeader>
              <SheetTitle>Notes — {notesRow?.vendor_name} · {notesRow?.vendor_account_number}</SheetTitle>
              <SheetDescription>Append-only notes for this missing-bill period.</SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto space-y-3 py-4">
              {notes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No notes yet.</p>
              ) : notes.map((n) => (
                <div key={n.id} className="rounded-md border p-3" data-testid={`mb-note-${n.id}`}>
                  <div className="text-sm">{n.body}</div>
                  <div className="text-xs text-muted-foreground mt-1">{n.author_name || 'Unknown'} · {fmtDate(n.created_at)}</div>
                </div>
              ))}
            </div>
            <div className="border-t pt-3 space-y-2">
              <textarea className="w-full border rounded-md p-2 text-sm min-h-[80px]"
                placeholder="Add a note (append-only)…" value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)} data-testid="mb-note-input" />
              <Button size="sm" className="w-full" onClick={addNote} disabled={savingNote || !noteBody.trim()} data-testid="mb-note-save">
                {savingNote ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Add Note
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Bill PDF drawer (slides from LEFT) */}
        <Sheet open={billOpen} onOpenChange={setBillOpen}>
          <SheetContent side="left" className="w-full sm:max-w-2xl flex flex-col" data-testid="mb-bill-drawer">
            <SheetHeader>
              <SheetTitle>Latest Bill — {billRow?.vendor_name} · {billRow?.vendor_account_number}</SheetTitle>
              <SheetDescription>PDF of the most recent received bill for this account.</SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-hidden py-3">
              {billLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground justify-center h-full">
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading bill…
                </div>
              ) : billPdfUrl ? (
                <iframe title="bill-pdf" src={billPdfUrl} className="w-full h-full rounded-md border" data-testid="mb-bill-iframe" />
              ) : (
                <p className="text-sm text-muted-foreground text-center mt-8">No PDF available for this bill.</p>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    </MainLayout>
  );
}
